import { ref } from 'vue'
import type { Ref } from 'vue'
import type { WizardPage, StoreWizardPagePayload, UpdateWizardPagePayload } from '@/types/wizard-forms'
import {
  listWizardPages as apiListWizardPages,
  createWizardPage as apiCreateWizardPage,
  updateWizardPage as apiUpdateWizardPage,
  deleteWizardPage as apiDeleteWizardPage,
  reorderWizardPages as apiReorderWizardPages,
} from '@/api/wizard-forms'

const pages = ref<WizardPage[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

async function fetchPages(formId: number): Promise<void> {
  loading.value = true
  error.value = null
  try {
    pages.value = await apiListWizardPages(formId)
  } catch {
    error.value = 'common.error_generic'
  } finally {
    loading.value = false
  }
}

async function createPage(formId: number, payload: StoreWizardPagePayload): Promise<WizardPage> {
  const created = await apiCreateWizardPage(formId, payload)
  pages.value = [...pages.value, created]
  return created
}

async function updatePage(
  formId: number,
  pageId: number,
  payload: UpdateWizardPagePayload,
): Promise<WizardPage> {
  const updated = await apiUpdateWizardPage(formId, pageId, payload)
  pages.value = pages.value.map((p) => (p.id === pageId ? updated : p))
  return updated
}

async function deletePage(formId: number, pageId: number): Promise<void> {
  await apiDeleteWizardPage(formId, pageId)
  pages.value = pages.value.filter((p) => p.id !== pageId)
}

async function reorderPages(formId: number, pageIds: number[]): Promise<void> {
  const reordered = await apiReorderWizardPages(formId, { page_ids: pageIds })
  pages.value = reordered
}

export function useWizardPages(): {
  pages: Ref<WizardPage[]>
  loading: Ref<boolean>
  error: Ref<string | null>
  fetchPages: (formId: number) => Promise<void>
  createPage: (formId: number, payload: StoreWizardPagePayload) => Promise<WizardPage>
  updatePage: (formId: number, pageId: number, payload: UpdateWizardPagePayload) => Promise<WizardPage>
  deletePage: (formId: number, pageId: number) => Promise<void>
  reorderPages: (formId: number, pageIds: number[]) => Promise<void>
} {
  return {
    pages,
    loading,
    error,
    fetchPages,
    createPage,
    updatePage,
    deletePage,
    reorderPages,
  }
}
